import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ExternalLink, MessageSquare, Plus, Search, X } from "lucide-react";
import { Avatar, Button, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtNum, fmtRub } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { useBloggers, useReplacePlacement, type Blogger, type Placement } from "@/shared/api/wb";

export function ReplacementModal({ placement, bloggers, existingBloggerIds, onClose, onReplaced }: {
  placement: Placement; bloggers: Map<string, Blogger>; existingBloggerIds: string[]; onClose: () => void;
  /** Called right before onClose once the replacement succeeds, so the
   * parent can surface a toast that survives this modal unmounting. */
  onReplaced?: (message: string) => void;
}) {
  const navigate = useNavigate();
  const replace = useReplacePlacement(placement.campaign_id);
  const { data: allBloggers } = useBloggers({});

  // Same picker UX as the regular "Добавить каналы" flow — search + platform
  // filter over every blogger not already in the campaign — instead of only
  // the (often empty) canned `placement.replacements` list, which left users
  // with no way to actually pick anyone.
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string>("all");

  function onReplace(newBloggerId: string, bloggerName: string) {
    replace.mutate({ placementId: placement.id, newBloggerId }, {
      onSuccess: () => {
        onReplaced?.(`${bloggerName} добавлен на замену · статус «Приглашён»`);
        onClose();
      },
    });
  }

  const declined = bloggers.get(placement.blogger_id);
  const reps = (placement.replacements ?? [])
    .map((id) => bloggers.get(id))
    .filter((b): b is Blogger => !!b);

  const catalog = (allBloggers ?? []).filter((b) => {
    if (existingBloggerIds.includes(b.id)) return false;
    if (platformFilter !== "all" && b.platform !== platformFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return b.name.toLowerCase().includes(q) || b.full.toLowerCase().includes(q);
    }
    return true;
  });
  const platforms = ["all", ...new Set((allBloggers ?? []).map((b) => b.platform))];

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center" style={{ background: "rgba(28,28,28,0.40)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="w-[560px] bg-white rounded-lg p-6 fade-up shadow-lg flex flex-col" style={{ maxHeight: "min(88vh, 760px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-danger mb-1.5">Замена</div>
            <h2 className="font-display font-extrabold text-[20px] tracking-tight">Канал отказался — вот варианты замены</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6"><X size={18} /></button>
        </div>

        {declined && (
          <div className="flex items-center gap-3 p-3 rounded-md bg-danger/5 border border-danger/15 mb-5">
            <Avatar name={declined.full} size={36} grad={declined.grad as [string, string]} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 font-display font-semibold text-[14px]">
                <PlatformIcon platform={declined.platform as Platform} size={12} /> {declined.name}
              </div>
              <div className="flex items-center gap-1 text-[12px] text-danger mt-0.5">
                <AlertCircle size={12} /> {placement.decline_reason ?? "Отказался"}
              </div>
            </div>
            <button
              onClick={() => { onClose(); navigate(`/inbox?campaign=${placement.campaign_id}`); }}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:border-ink flex-shrink-0"
            >
              <MessageSquare size={13} /> Связаться
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
          {reps.length > 0 && (
            <>
              <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-2">Рекомендуем</div>
              <div className="grid gap-2 mb-5">
                {reps.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 p-2.5 rounded-md bg-cream">
                    <Avatar name={b.full} size={32} grad={b.grad as [string, string]} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 font-display font-semibold text-[13.5px]">
                        <PlatformIcon platform={b.platform as Platform} size={11} /> {b.name}
                      </div>
                      <div className="text-[11px] text-ink-40">{fmtNum(b.reach)} охват · CPV {b.cpv}₽ · {fmtRub(b.price)}</div>
                    </div>
                    <Button size="sm" onClick={() => onReplace(b.id, b.name)} disabled={replace.isPending}><Plus size={12} strokeWidth={3} /> Заменить</Button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Same picker as "Добавить каналы": search + platform filter over
              the full catalog, so replacement never dead-ends on an empty
              recommendation list. */}
          <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-2">
            {reps.length > 0 ? "Или выберите из каталога" : "Выберите канал на замену"}
          </div>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-30 pointer-events-none" />
              <input
                type="text"
                placeholder="Поиск по имени или каналу…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-8 pl-8 pr-3 rounded-md border border-ink-12 bg-white text-[12.5px] outline-none focus:border-ink"
              />
            </div>
            <div className="flex gap-1">
              {platforms.slice(0, 5).map((p) => (
                <button
                  key={p}
                  onClick={() => setPlatformFilter(p)}
                  className={cn(
                    "h-8 px-3 rounded-pill text-[12px] font-display font-semibold transition-colors",
                    platformFilter === p ? "bg-ink text-white" : "bg-ink-6 text-ink-60 hover:text-ink",
                  )}
                >
                  {p === "all" ? "Все" : p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-1.5 mb-2">
            {catalog.map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-ink-6 transition-colors">
                <Avatar name={b.full} size={28} grad={b.grad as [string, string]} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[13px] font-display font-semibold">
                    <PlatformIcon platform={b.platform as Platform} size={11} /> {b.name}
                  </div>
                  <div className="text-[11px] text-ink-40 truncate">{b.full} · {fmtNum(b.reach)} · ~{fmtRub(b.price)}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => onReplace(b.id, b.name)} disabled={replace.isPending}>
                  <Plus size={12} strokeWidth={3} /> Заменить
                </Button>
              </div>
            ))}
            {catalog.length === 0 && (
              <div className="py-4 text-center text-[13px] text-ink-40">Нет блогеров по фильтру</div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t border-ink-12 flex-shrink-0">
          <button
            onClick={() => { onClose(); navigate("/catalog", { state: { preselectedCampaignId: placement.campaign_id } }); }}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink"
          >
            <ExternalLink size={14} /> Перейти в каталог
          </button>
          <Button variant="outline" onClick={onClose}>Отмена</Button>
        </div>
      </div>
    </div>
  );
}
